import { useEffect } from 'react'

import { Formik, FormikHelpers } from 'formik'

import useAppQueryState from '@hooks/useAppQueryState'
import useInscalarTrackEvent from '@hooks/useInscalarTrackEvent'
import useLogEvents from '@hooks/useLogEvents'

import { useMinimumGuaranteedCreditLimitDeferMutation } from '@queries/useMinimumGuaranteedCreditLimitDeferMutation'
import { useMinimumGuaranteedCreditLimitMutation } from '@queries/useMinimumGuaranteedCreditLimitMutation'

import GuaranteedCreditLimitIntroForm from './components/GuaranteedCreditLimitIntroForm'
import {
    GuaranteedCreditLimitIntroFormSchema,
    GuaranteedCreditLimitIntroFormValues,
} from './components/GuaranteedCreditLimitIntroForm/GuaranteedCreditLimitIntroForm.validation'

interface GuaranteedCreditLimitIntroFormWrapperProps {
    onFormClose: () => void
}

const GuaranteedCreditLimitIntroFormWrapper = ({
    onFormClose,
}: GuaranteedCreditLimitIntroFormWrapperProps) => {
    const { mutateAsync: postGCL } = useMinimumGuaranteedCreditLimitMutation()
    const { mutateAsync: postDefer } =
        useMinimumGuaranteedCreditLimitDeferMutation()
    const logEvents = useLogEvents()
    const { trackSubmit } = useInscalarTrackEvent()
    const { appQueryState } = useAppQueryState()

    async function onFindCardsPress(
        values: GuaranteedCreditLimitIntroFormValues,
        formikHelpers: FormikHelpers<GuaranteedCreditLimitIntroFormValues>
    ) {
        const minCreditLimit = values.showCustomField
            ? values.minCreditLimitCustom
            : values.minCreditLimitOption

        if (minCreditLimit == null) {
            return
        }

        formikHelpers.setStatus(undefined)

        try {
            await postGCL({
                minimumGuaranteedCreditLimit: Number(minCreditLimit),
                customerId: appQueryState.customerId!,
            })

            logEvents.logMinCreditLimitSubmit({
                minCreditLimit,
                source: 'GuaranteedCreditLimitIntro',
            })

            trackSubmit({
                step: 'credit_limit_request',
                journey: 'credit_cards',
                journeyCategory: 'credit_cards',
            })

            onFormClose()
        } catch (err) {
            formikHelpers.setStatus(
                'Failed to set your ideal credit limit. Please try again.'
            )
        }
    }

    async function onCardsTableContinuePress() {
        logEvents.logMinCreditLimitSkip({
            source: 'GuaranteedCreditLimitIntro',
        })

        await postDefer({
            customerId: appQueryState.customerId!,
        })

        onFormClose()
    }

    useEffect(() => {
        logEvents.logCreditLimitIntroView()
    }, [])

    return (
        <Formik
            onSubmit={onFindCardsPress}
            initialValues={
                {
                    minCreditLimitCustom: null,
                    minCreditLimitOption: null,
                } as GuaranteedCreditLimitIntroFormValues
            }
            validateOnChange={true}
            validationSchema={GuaranteedCreditLimitIntroFormSchema}
        >
            <GuaranteedCreditLimitIntroForm
                onFormClose={onCardsTableContinuePress}
            />
        </Formik>
    )
}

export default GuaranteedCreditLimitIntroFormWrapper
